import { PixelCrop } from 'react-image-crop'

// JPEG quality of a crop capped by `maxOutputSize`: small enough for a profile photo, no visible loss.
const CAPPED_OUTPUT_QUALITY = 0.85

/**
 * Crops the image. With `maxOutputSize` the crop is scaled down so its longer side fits it and
 * comes out as JPEG — a phone photo goes from megabytes to a profile picture's size.
 */
export async function cropImage(
  imageSrc: string,
  pixelCrop: PixelCrop,
  fileName: string,
  mimeType = 'image/jpeg',
  maxSizeBytes?: number,
  maxOutputSize?: number
): Promise<File> {
  const image = await loadImageElement(imageSrc)

  const scale = maxOutputSize ? Math.min(1, maxOutputSize / Math.max(pixelCrop.width, pixelCrop.height)) : 1
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(pixelCrop.width * scale)
  canvas.height = Math.round(pixelCrop.height * scale)

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas context')

  // JPEG has no transparency: a transparent PNG would come out with a black background.
  if (maxOutputSize) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height)

  const toBlob = (type: string, quality?: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) reject(new Error('Canvas is empty'))
          else resolve(blob)
        },
        type,
        quality
      )
    })

  const jpegName = fileName.match(/\.[^.]+$/) ? fileName.replace(/\.[^.]+$/, '.jpg') : `${fileName}.jpg`

  if (maxOutputSize) {
    return new File([await toBlob('image/jpeg', CAPPED_OUTPUT_QUALITY)], jpegName, { type: 'image/jpeg' })
  }

  const normalizedMimeType = mimeType === 'image/jpg' ? 'image/jpeg' : mimeType

  let blob = await toBlob(normalizedMimeType)
  let outputMime = normalizedMimeType
  let outputName = fileName

  // Canvas re-encodes PNG as uncompressed 32-bit RGBA, which can inflate palette-based
  // PNGs well beyond their original size. Fall back to JPEG when the result exceeds the limit.
  if (maxSizeBytes && blob.size > maxSizeBytes && outputMime !== 'image/jpeg') {
    outputMime = 'image/jpeg'
    outputName = jpegName

    let quality = 0.92
    do {
      blob = await toBlob('image/jpeg', quality)
      quality -= 0.07
    } while (blob.size > maxSizeBytes && quality >= 0.5)
  }

  return new File([blob], outputName, { type: outputMime })
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', reject)
    img.src = src
  })
}
